import {
  Box,
  Card,
  Flex,
  Grid,
  Heading,
  SegmentedControl,
  Text,
} from '@radix-ui/themes';
import { SessionRecapHelper } from 'classes/helpers/session-recap.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { usePlotHover } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/plot-hover';
import { RecapFieldView } from 'components/sections/analytics/session-hits/recap/visualizations/field-view';
import { RecapColorLegend } from 'components/sections/analytics/session-hits/recap/visualizations/legend';
import { RecapStrikeZoneView } from 'components/sections/analytics/session-hits/recap/visualizations/strike-zone-view';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { HittersContext } from 'contexts/hitters.context';
import { RecapColorBy } from 'enums/session-recap/enums';
import { t } from 'i18next';
import { ISessionRecapHit } from 'interfaces/session-recap/i-session-recap-hit';
import { HitterSide } from 'lib_ts/enums/hitters.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IStrikeZone } from 'lib_ts/interfaces/i-strike-zone';
import { useCallback, useContext, useMemo, useState } from 'react';

interface IProps {
  hitterID: string;
  records: ISessionRecapHit[];
  szDef: IStrikeZone;
}

const toggleLegendKey = (key: string) => {
  const current = useAnalyticsStore.getState().legendKeys ?? [];

  const next = current.includes(key)
    ? current.filter((k) => k !== key) // toggle off
    : [...current, key]; // toggle on

  useAnalyticsStore.setState({ legendKeys: next.length ? next : null });
};

export const SessionRecapVisualizations = (props: IProps) => {
  const { hoveredItemKey } = usePlotHover();
  const hoveredHitID = hoveredItemKey ?? null;

  const colorByID = useAnalyticsStore((s) => s.colorByID);
  const legendKeys = useAnalyticsStore((s) => s.legendKeys);
  const selectedHitID = useAnalyticsStore((s) => s.selectedHitID);
  const zoneFilter = useAnalyticsStore((s) => s.zoneFilter);

  const [hoveredLegendKey, setHoveredLegendKey] = useState<string | null>(null);
  const onLegendEnter = useCallback((key: string) => {
    setHoveredLegendKey(key);
  }, []);
  const onLegendLeave = useCallback((key: string) => {
    setHoveredLegendKey((current) => (current === key ? null : current));
  }, []);

  const { hitters } = useContext(HittersContext);

  const hitter = useMemo(
    () => hitters.find((h) => h._id === props.hitterID),
    [props.hitterID, hitters]
  );

  const hitterSide = hitter?.side ?? HitterSide.RHH;

  const chartRecords = useMemo(() => {
    if (!legendKeys && !zoneFilter) {
      return props.records;
    }

    const selected = legendKeys ? new Set(legendKeys) : null;

    return props.records.filter((r) => {
      if (r.hit._id === hoveredHitID || r.hit._id === selectedHitID) {
        return true;
      }

      if (selected) {
        const key = SessionRecapHelper.getLegendKey(r, colorByID);

        if (!key || !selected.has(key)) {
          return false;
        }
      }

      return (
        !zoneFilter ||
        SessionRecapHelper.isInChartZone(r, zoneFilter, hitterSide, props.szDef)
      );
    });
  }, [
    props.records,
    props.szDef,
    legendKeys,
    zoneFilter,
    colorByID,
    hoveredHitID,
    selectedHitID,
    hitterSide,
  ]);

  return (
    <ErrorBoundary identifier="SessionRecapVisualizations">
      <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
        <Flex
          justify="between"
          align="center"
          gap={RADIX.FLEX.GAP.SM}
          wrap="wrap"
        >
          <Heading size={RADIX.HEADING.SIZE.SM}>
            {t('hitters.recap-visualizations')}
          </Heading>

          <Flex gap={RADIX.FLEX.GAP.SM} align="center">
            <Text size="1" color={RADIX.COLOR.SECONDARY}>
              {t('hitters.recap-color-by')}
            </Text>
            <SegmentedControl.Root
              size="1"
              value={colorByID}
              onValueChange={(v) =>
                useAnalyticsStore.setState({
                  colorByID: v as RecapColorBy,
                  legendKeys: null,
                })
              }
            >
              {Object.values(RecapColorBy).map((v) => (
                <SegmentedControl.Item key={v} value={v}>
                  {t(SessionRecapHelper.getLabelForColorBy(v))}
                </SegmentedControl.Item>
              ))}
            </SegmentedControl.Root>
          </Flex>
        </Flex>

        <Card className="recap-card">
          <Grid columns={{ initial: '1', md: '2' }} gap={RADIX.FLEX.GAP.NONE}>
            <Box className="recap-panel">
              <RecapFieldView
                records={props.records}
                shots={chartRecords}
                hitterSide={hitterSide}
                highlightKey={hoveredLegendKey}
              />
            </Box>
            <Box className="recap-panel">
              <RecapStrikeZoneView
                records={props.records}
                shots={chartRecords}
                szDef={props.szDef}
                highlightKey={hoveredLegendKey}
              />
            </Box>
          </Grid>
        </Card>

        <RecapColorLegend
          colorBy={colorByID}
          records={props.records}
          activeKeys={legendKeys}
          onToggle={toggleLegendKey}
          onClear={() => useAnalyticsStore.setState({ legendKeys: null })}
          onHoverStart={onLegendEnter}
          onHoverEnd={onLegendLeave}
        />
      </Flex>
    </ErrorBoundary>
  );
};
